import {
    DropdownContent,
    DropdownMenu,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownTrigger,
    IconButton,
    Kebab,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@bcc-ui/ui-components";
import classNames from "classnames";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Row } from "react-table";

import DeleteConfirmationDialog from "components/confirmation/DeleteConfirmationDialog";
import Warning from "components/icons/Warning";
import { subpageContext } from "components/layout/ApplicationLayout";
import { CorporateBundle, ProcessorBundle } from "components/licenses/bundles";
import { FEATURE_TENANT_MANAGEMENT, fetchDynamicTierLicenses, getFeatureLicense } from "components/licenses/common";
import AddLicenseDeliveryView, {
    showTooltipForDeliveryButton,
} from "components/licenses/delivery-history/AddLicenseDeliveryView";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import Modal from "components/modal/Modal";
import { DELIVERYHISTORY_LICENSES_ROUTE } from "components/router/Routes";
import AddTenantView from "components/tenants/add-tenant/AddTenantView";
import EditTenantView from "components/tenants/edit-tenant/EditTenantView";
import EditMcConfigurationLoader from "components/tenants/EditMcConfigurationLoader";
import MoveTenantDialog from "components/tenants/MoveTenantDialog";
import ViewTenantHierarchy from "components/tenants/tenant-hierarchy/ViewTenantHierarchy";
import style from "components/tenants/tenants.scss";
import Heading from "components/typography/heading/Heading";
import {
    AUTH_LICENSE_ALERT,
    AUTH_LICENSE_ASSIGN,
    AUTH_TENANT_ACCESS,
    AUTH_TENANT_CREATE,
    AUTH_TENANT_DELETE,
    AUTH_TENANT_EDIT,
    AUTH_TENANT_VIEW,
} from "domain/authority";
import { SUPPORT_EMAIL } from "domain/globalConstants";
import { calculateTrialDaysLeft, ContractType, Tenant, TenantListItem } from "domain/tenants";
import { licenseService, ProductToRateList } from "services/licenses/LicenseService";
import { setTenantAccessUrls } from "services/login/endpointRepository";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { hasSubTenantCookie, hasTenantCookie, isCurrentUserInternal } from "services/tenants/tenantCookieService";
import { tenantService } from "services/tenants/TenantService";
import { userSessionService } from "services/user/UserSessionService";
import { StoreState } from "store";
import { pushTenantDetails } from "store/tenantDetails";
import { switchTheme } from "store/theme";
import buttons from "styles/buttons.scss";
import form from "styles/form.scss";
import { isTextTruncated } from "utils/commonFunctions";
import { formatDateWithoutTime, toUtcEndOfDay } from "utils/format";

import testIds from "testIds.json";

const TENANT_MAX_LEVEL = 5;

interface Result {
    title: string;
    message: string;
    resultVisible: boolean;
}

interface TenantMenuItem {
    authority: boolean;
    title: string;
    titleParams?: Record<string, string | number>;
    onClick: () => void;
    testId: string;
    disabled?: boolean;
    errorStyle?: boolean;
    dataInfo?: Record<string, string | boolean>;
}

interface Props {
    tenant: Tenant;
    onTenantEdit: () => void;
    onTenantDelete: () => void;
    licenseCount: number;
    userCount: number;
    workflowCount: number;
    reportCount: number;
    parentLevel: number;
    rowsUntilSubTenant: Row<Tenant>[];
    setNewComponent: (value: boolean) => void;
    setTenantUuid: (value: string) => void;
    setTenantName: (value: string) => void;
    deliveryAllowed: boolean;
    rateVersions?: ProductToRateList[];
    parentContractType: ContractType;
    rowTenantLevel: number;
    tenants: TenantListItem[];
    tableContainerRef: React.RefObject<HTMLDivElement>;
}

interface KebabMenuItemTooltipProps {
    children: React.ReactNode;
}

const KebabMenuItemTooltip = (props: KebabMenuItemTooltipProps) => {
    const ref = React.useRef<HTMLSpanElement>(null);
    const [isTruncated, setIsTruncated] = React.useState(false);

    React.useLayoutEffect(() => {
        const element = ref.current;
        if (!element) {
            return;
        }
        const update = () => setIsTruncated(isTextTruncated(element));
        update();
        const resizeObserver = new ResizeObserver(update);
        resizeObserver.observe(element);
        return () => resizeObserver.disconnect();
    }, [props.children]);

    return (
        <Tooltip placement="bottom">
            {isTruncated && <TooltipContent>{props.children}</TooltipContent>}
            <TooltipTrigger className={style.tooltipTrigger}>
                <span className={style.menuItemLabel} ref={ref}>
                    {props.children}
                </span>
            </TooltipTrigger>
        </Tooltip>
    );
};

export const TenantKebabMenu = (props: Props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { current: abortControllers } = React.useRef<AbortController[]>([]);
    const user = useSelector((state: StoreState) => state.userReducer.user);
    const theme = useSelector((state: StoreState) => state.themeReducer.theme);

    const context = React.useContext(subpageContext);
    const hideResultAndRedirectToRoot = () => {
        setResult({ title: result.title, message: result.message, resultVisible: false });

        if (hasTenantCookie()) {
            window.location.replace(location.pathname);
        }

        props.onTenantEdit();
        props.onTenantDelete();
    };

    const [deleteTenantModalVisible, setDeleteTenantModalVisible] = React.useState(false);
    const [subTenantModalVisible, setSubTenantModalVisible] = React.useState(false);
    const [addTenantModalVisible, setAddTenantModalVisible] = React.useState(false);
    const [retryModalVisible, setRetryModalVisible] = React.useState(false);
    const [licenseNotAvailable, setLicenseNotAvailable] = React.useState(false);
    const [result, setResult] = React.useState<Result>({
        title: "",
        message: "",
        resultVisible: false,
    });
    const [accessInProgress, setAccessInProgress] = React.useState(false);
    const [accessTenantModalVisible, setAccessTenantModalVisible] = React.useState(false);

    const [isMcConfigurationVisible, setIsMcConfigurationVisible] = React.useState(false);
    const [moveTenantModalVisible, setMoveTenantModalVisible] = React.useState(false);
    const subtenantCreationAllowed: boolean =
        props.tenant.contractType === "TRIAL" ? (calculateTrialDaysLeft(props.tenant) ?? 0) > 0 : true;

    const fetchData = () => {
        setAccessInProgress(true);
        const landingPage = user?.default_settings.tenant_access_default_view;
        const abortController = new AbortController();
        abortControllers.push(abortController);
        if (props.rowsUntilSubTenant.length > 0) {
            props.rowsUntilSubTenant.forEach((eachRow) => {
                const tenant = eachRow.original;
                dispatch(
                    pushTenantDetails({
                        uuid: tenant.uuid,
                        featureLicenses: [],
                        region: tenant.region,
                        type: tenant.type,
                        tenantName: tenant.name,
                        loginMethod: tenant.loginMethod,
                        tenantTier: tenant.tier,
                        licensingModel: tenant.licensingModel,
                        parentDrawBackPrevention: tenant.parentDrawbackPrevention,
                        contractType: tenant.contractType,
                        expirationDate: tenant.expirationDate,
                    })
                );
            });
        }
        tenantService
            .accessTenant(props.tenant.uuid, abortController)
            .then((data) => {
                setTenantAccessUrls(
                    data.stan,
                    data.laurel,
                    data.arthur,
                    data.jefferson,
                    data.oliver,
                    data.publicApi,
                    data.publicApiDocumentation
                );
                dispatch(
                    pushTenantDetails({
                        uuid: props.tenant.uuid,
                        featureLicenses: data.featureLicenses,
                        region: data.region,
                        type: data.type,
                        tenantName: data.tenantName,
                        loginMethod: data.loginMethod,
                        tenantTier: data.tenantTier,
                        licensingModel: data.licensingModel,
                        parentDrawBackPrevention: data.parentDrawbackPrevention,
                        contractType: data.contractType,
                        expirationDate: data.expirationDate,
                    })
                );
                dispatch(switchTheme());
                const checkFeatureLicense = getFeatureLicense();
                const checkIfFeatureLicensePresent: boolean = checkFeatureLicense.has(landingPage)
                    ? data.featureLicenses.some((item) => checkFeatureLicense.get(landingPage)?.includes(item))
                    : true;
                hasTenantCookie() &&
                    window.location.replace(
                        !checkIfFeatureLicensePresent
                            ? DELIVERYHISTORY_LICENSES_ROUTE.path
                            : user?.default_settings.tenant_access_default_view
                              ? user?.default_settings.tenant_access_default_view
                              : "/"
                    );
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    setResult({
                        title: t("AccessCustomerView.accessTitle"),
                        message: t("AccessCustomerView.accessFailureMessage", { customerName: props.tenant.name }),
                        resultVisible: true,
                    });
                }
            })
            .finally(() => {
                setAccessInProgress(false);
                setAccessTenantModalVisible(false);
            });
    };

    const [okClicked, setOkClicked] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [hierarchyVisibility, setHierarchyVisibility] = React.useState(false);

    const [checkHierarchy, setCheckHierarchy] = React.useState(false);

    const handleTenantDelete = () => {
        setOkClicked(true);
        deleteTenant();
    };

    const deleteTenant = async () => {
        const abortController = new AbortController();
        abortControllers.push(abortController);
        const { signal } = abortController;
        try {
            await tenantService.deleteTenant(props.tenant.uuid, props.tenant.region, abortController);
        } catch {
            if (!signal.aborted) {
                setOkClicked(false);
                setDeleteTenantModalVisible(false);
                setSubTenantModalVisible(false);
                setRetryModalVisible(true);
            }
            return;
        }
        if (signal.aborted) {
            return;
        }
        setDeleteTenantModalVisible(false);
        setSubTenantModalVisible(false);
        setRetryModalVisible(false);
        setOkClicked(false);
        setResult({
            title: t("DeleteTenant.inProgressTitle"),
            message: t("DeleteTenant.inProgressMessage"),
            resultVisible: true,
        });
    };

    const checkTenantHierarchy = async () => {
        setLoading(true);
        const abortController = new AbortController();
        abortControllers.push(abortController);
        await tenantService
            .fetchHierarchy(props.tenant.uuid, abortController)
            .then((response) => {
                response?.tenants.length == 0 || undefined
                    ? setDeleteTenantModalVisible(true)
                    : setSubTenantModalVisible(true);
                setLoading(false);
            })
            .catch(() => {
                setCheckHierarchy(true);
                setRetryModalVisible(true);
                setLoading(false);
            });
    };

    function updateState() {
        context?.setSubpage({
            content: undefined,
        });
        setLoading(false);
        setAddTenantModalVisible(false);
    }

    function updateStateWhenLicenseAdded() {
        setLoading(false);
        setDeliveryCreationModalVisible(false);
    }

    const [deliveryCreationModalVisible, setDeliveryCreationModalVisible] = React.useState(false);

    const subject = encodeURIComponent(
        "Blancco Management Portal - Unexpected Error while deleting tenant : " + props.tenant.name
    );
    const body = encodeURIComponent(
        "Hi there\n\n" +
            "I've experienced a persistent issue with deleting tenant: " +
            props.tenant.name +
            "\nWould you be able to assist?\n\n"
    );
    const mailToLink = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;

    async function showAddTenantFormModal() {
        setLoading(true);
        usageStatisticsService.sendEvent({
            category: Category.TENANT,
            action: Action.ADD_SUB_TENANT,
        });
        let foundTenantManagementLicense = false;
        if (props.tenant.tier != undefined) {
            const tierLicenses = fetchDynamicTierLicenses(props.tenant.tier);
            if (tierLicenses != undefined && tierLicenses.includes(FEATURE_TENANT_MANAGEMENT)) {
                foundTenantManagementLicense = !(
                    props.tenant.tier === CorporateBundle.POWER || props.tenant.tier === ProcessorBundle.PRO
                );
            }
        }
        const licenseResponse = await licenseService.fetchLicenses(
            licensesAbortController,
            undefined,
            undefined,
            props.tenant.uuid
        );
        if (
            foundTenantManagementLicense ||
            licenseResponse.licenses.some((each) => each.type === FEATURE_TENANT_MANAGEMENT)
        ) {
            setLoading(false);
            setAddTenantModalVisible(true);
        } else {
            setLoading(false);
            setLicenseNotAvailable(true);
        }
    }

    const licensesAbortController = new AbortController();
    abortControllers.push(licensesAbortController);

    const hideErrorModal = () => {
        setLicenseNotAvailable(false);
    };

    const deliveryItemId = "deliveryItemButton";

    const parentExpirationDate = props.tenant.expirationDate
        ? new Date(toUtcEndOfDay(new Date(formatDateWithoutTime(props.tenant.expirationDate))))
        : undefined;
    {
        addTenantModalVisible &&
            context?.setSubpage({
                content: (
                    <AddTenantView
                        onHideModal={updateState}
                        onTenantAdded={updateState}
                        parentExpirationDate={parentExpirationDate}
                        parentLevel={props.rowTenantLevel}
                        parentLicenses={undefined}
                        parentUuid={props.tenant.uuid}
                        tier={props.tenant.tier}
                        type={props.tenant.type}
                        licensingModel={props.tenant.licensingModel}
                        rateVersions={props.rateVersions}
                        parentDrawbackPrevention={props.tenant.parentDrawbackPrevention}
                        parentContractType={props.parentContractType}
                    />
                ),
                isModal: false,
            });
    }

    const menuItems: TenantMenuItem[] = [
        {
            authority:
                userSessionService.hasFeatureLicense("FEATURE_TENANT_MANAGEMENT") &&
                userSessionService.userHasAllAuthorities([AUTH_TENANT_ACCESS]),
            title: "AccessCustomerView.accessCustomerAccount",
            onClick: () => {
                setAccessTenantModalVisible(true);
                usageStatisticsService.sendEvent({
                    category: Category.TENANT,
                    action: Action.ACCESS_TENANT,
                });
            },
            testId: testIds.workArea.tenant.table.kebabMenu.accessButton,
        },
        {
            authority:
                userSessionService.hasFeatureLicense("FEATURE_TENANT_MANAGEMENT") &&
                userSessionService.userHasAllAuthorities([AUTH_TENANT_EDIT]),
            title: "EditCustomerView.edit",
            onClick: () => {
                context?.setSubpage({
                    content: (
                        <EditTenantView
                            customerName={props.tenant.name}
                            status={props.tenant.status}
                            uuid={props.tenant.uuid}
                            tier={props.tenant.tier}
                            tenantRegion={props.tenant.region}
                            expirationDate={props.tenant.expirationDate}
                            countryCode={props.tenant.countryCode}
                            contactName={props.tenant.contactName}
                            contactEmail={props.tenant.contactEmail}
                            creationDate={props.tenant.createdTimestamp}
                            salesforceLeadId={props.tenant.salesforceLeadId}
                            notes={props.tenant.notes}
                            salesforceAccountId={props.tenant.salesforceAccountId}
                            parentExpirationDate={props.tenant.parentExpirationDate}
                            tenantType={props.tenant.type}
                            licensingModel={props.tenant.licensingModel}
                            parentLicensingModel={props.tenant.parentLicensingModel}
                            parentType={props.tenant.parentType}
                            parentTier={props.tenant.parentTier}
                            salesforceContractState={props.tenant.salesforceContractState}
                            salesforceContractId={props.tenant.salesforceContractId}
                            salesforceContactEmail={props.tenant.salesforceContactEmail}
                            salesforceContractEndDate={props.tenant.salesforceContractEndDate}
                            parentDrawbackPrevention={props.tenant.parentDrawbackPrevention}
                            drawbackPrevention={props.tenant.drawbackPrevention}
                            contractType={props.tenant.contractType}
                            automaticDeletion={props.tenant.automaticDeletion}
                            parentContractType={props.parentContractType}
                            diskQuotaMib={props.tenant.diskQuotaMib}
                            uploadLimitMib={props.tenant.uploadLimitMib}
                            downloadLimitMib={props.tenant.downloadLimitMib}
                        />
                    ),
                    isModal: false,
                });
            },
            testId: testIds.workArea.tenant.table.kebabMenu.editButton,
        },
        {
            authority:
                userSessionService.hasFeatureLicense("FEATURE_TENANT_MANAGEMENT") &&
                userSessionService.userHasAllAuthorities([AUTH_TENANT_EDIT]) &&
                isCurrentUserInternal() &&
                props.tenant.status,
            title: "EditCustomerView.move.move",
            onClick: () => {
                usageStatisticsService.sendEvent({
                    category: Category.TENANT,
                    action: Action.MOVE_TENANT,
                });
                setMoveTenantModalVisible(true);
            },
            testId: testIds.workArea.tenant.table.kebabMenu.moveButton,
        },
        {
            authority: isCurrentUserInternal() && userSessionService.userHasAllAuthorities([AUTH_TENANT_EDIT]),
            title: "EditCustomerView.editMcConfiguration",
            onClick: () => {
                usageStatisticsService.sendEvent({
                    category: Category.TENANT,
                    action: Action.EDIT_MC_CONFIGURATION,
                });
                setIsMcConfigurationVisible(true);
            },
            testId: testIds.workArea.tenant.table.kebabMenu.mcSettingsButton,
        },
        {
            authority:
                userSessionService.hasFeatureLicense("FEATURE_TENANT_MANAGEMENT") &&
                userSessionService.userHasAllAuthorities([
                    AUTH_TENANT_VIEW,
                    AUTH_TENANT_EDIT,
                    AUTH_TENANT_ACCESS,
                    AUTH_TENANT_CREATE,
                ]),
            title: "ViewTenantHierarchy.iconTooltip",
            onClick: () => {
                setHierarchyVisibility(true);
                usageStatisticsService.sendEvent({
                    category: Category.TENANT,
                    action: Action.VIEW_TENANT_HIERARCHY,
                });
            },
            testId: testIds.workArea.tenant.table.kebabMenu.hierarchyButton,
        },
        {
            authority:
                userSessionService.hasFeatureLicense("FEATURE_TENANT_MANAGEMENT") &&
                userSessionService.userHasAllAuthorities([AUTH_LICENSE_ASSIGN, AUTH_TENANT_ACCESS]),
            title: "AddLicenseDelivery.title",
            onClick: () => {
                setDeliveryCreationModalVisible(true);
                usageStatisticsService.sendEvent({
                    category: Category.LICENSE_DELIVERY,
                    action: Action.ADD_LICENSE_DELIVERY,
                });
            },
            testId: testIds.workArea.tenant.table.kebabMenu.createDeliveryButton,
            disabled: !props.deliveryAllowed,
            dataInfo: { "data-for": deliveryItemId, "data-tip": true },
        },
        {
            authority:
                userSessionService.hasFeatureLicense("FEATURE_TENANT_MANAGEMENT") &&
                userSessionService.userHasAllAuthorities([AUTH_TENANT_CREATE, AUTH_TENANT_ACCESS]) &&
                (hasTenantCookie() || hasSubTenantCookie()
                    ? (props.tenant.depth ?? 1) + props.parentLevel < TENANT_MAX_LEVEL
                    : (props.tenant.depth ?? 1) < TENANT_MAX_LEVEL - 1) &&
                subtenantCreationAllowed,
            title: "Common.addSubTenant",
            onClick: showAddTenantFormModal,
            testId: testIds.workArea.tenant.table.kebabMenu.createTenantButton,
        },
        {
            authority:
                userSessionService.hasFeatureLicense("FEATURE_LICENSE_ALERTS") &&
                userSessionService.userHasAllAuthorities([AUTH_LICENSE_ALERT, AUTH_TENANT_ACCESS]),
            title: "LicenseAlerts.create",
            onClick: () => {
                props.setNewComponent(true);
                props.setTenantUuid(props.tenant.uuid);
                props.setTenantName(props.tenant.name);
            },
            testId: testIds.workArea.tenant.table.kebabMenu.createLicenseAlertButton,
        },
        {
            authority:
                userSessionService.hasFeatureLicense("FEATURE_TENANT_MANAGEMENT") &&
                userSessionService.userHasAllAuthorities([AUTH_TENANT_DELETE]),
            title: "CustomersTable.deleteCustomer",
            titleParams: { customerName: props.tenant.name },
            onClick: () => {
                checkTenantHierarchy();
                usageStatisticsService.sendEvent({
                    category: Category.TENANT,
                    action: Action.DELETE_TENANT,
                });
            },
            testId: testIds.workArea.tenant.table.kebabMenu.deleteButton,
            errorStyle: true,
        },
    ];

    return (
        <>
            {showTooltipForDeliveryButton(props.deliveryAllowed, t, deliveryItemId)}
            <DropdownMenu
                position={"bottom-start"}
                flipOptions={{
                    fallbackPlacements: ["top-start"],
                }}
                closeOnSelection
            >
                <DropdownTrigger>
                    <IconButton testId={testIds.common.primaryView.table.kebabMenu.button}>
                        <Kebab />
                    </IconButton>
                </DropdownTrigger>
                <DropdownContent containerRef={props.tableContainerRef}>
                    {menuItems.map((item, index) => (
                        <>
                            <DropdownMenuItem
                                key={item.testId}
                                onClick={item.onClick}
                                testId={item.testId}
                                disabled={item.disabled}
                                errorStyle={item.errorStyle}
                                {...item.dataInfo}
                            >
                                <KebabMenuItemTooltip>{t(item.title, item.titleParams)}</KebabMenuItemTooltip>
                            </DropdownMenuItem>
                            {index === menuItems.length - 2 && <DropdownMenuSeparator />}
                        </>
                    ))}
                </DropdownContent>
            </DropdownMenu>
            <Modal
                isOpen={accessTenantModalVisible}
                hideModal={() => setAccessTenantModalVisible(false)}
                modalTitle={t("AccessCustomerView.title")}
            >
                <div className={style.resultContainer}>
                    {t("AccessCustomerView.message", { customerName: props.tenant.name })}
                </div>

                {accessInProgress ? (
                    <LoadingIndicator />
                ) : (
                    <div className={buttons.buttonContainer}>
                        <button
                            className={buttons.secondaryButtonWithoutIcon}
                            onClick={() => setAccessTenantModalVisible(false)}
                        >
                            {t("Common.cancel")}
                        </button>

                        <button className={buttons.okOptionalButtonPrimary} onClick={fetchData}>
                            {t("Common.ok")}
                        </button>
                    </div>
                )}
            </Modal>
            <Modal isOpen={result.resultVisible} hideModal={hideResultAndRedirectToRoot} modalTitle={result.title}>
                <div className={style.resultContainer}>{result.message}</div>
                <div className={style.warningContainer}>
                    <button
                        className={buttons.primaryOkButton}
                        onClick={hideResultAndRedirectToRoot}
                        data-testid={testIds.common.confirmationDialog.confirmButton}
                    >
                        {t("Common.ok")}
                    </button>
                </div>
            </Modal>
            <Modal
                isOpen={hierarchyVisibility}
                hideModal={() => setHierarchyVisibility(false)}
                modalTitle={t("ViewTenantHierarchy.title", { tenantName: props.tenant.name })}
            >
                {<ViewTenantHierarchy uuid={props.tenant.uuid} />}
            </Modal>
            <DeleteConfirmationDialog
                modalDisplayed={deleteTenantModalVisible}
                introductionMessage={
                    <div>
                        <p>
                            {t("DeleteTenant.introductionMessagePre")}
                            <b>{props.tenant.name}</b>
                            {props.tenant.expirationDate == undefined || props.tenant.expirationDate == ""
                                ? t("DeleteTenant.introductionMessageWithoutDate")
                                : t("DeleteTenant.introductionMessageWithDate", {
                                      expDate: formatDateWithoutTime(props.tenant.expirationDate),
                                  })}
                            <ul className={style.listStyleType}>
                                <li>{t("DeleteTenant.licenseCount", { licenseCount: props.licenseCount })}</li>
                                <li>{t("DeleteTenant.userCount", { userCount: props.userCount })}</li>
                                <li>{t("DeleteTenant.reportCount", { reportCount: props.reportCount })}</li>
                                <li>
                                    {t("DeleteTenant.workflowCount", {
                                        workflowCount: props.workflowCount,
                                    })}
                                </li>
                            </ul>
                            {t("DeleteTenant.introductionMessagePost")}
                        </p>
                    </div>
                }
                confirmationMessage={t("DeleteTenant.confirmationMessage")}
                handleCommand={handleTenantDelete}
                setModalDisplayed={() => setDeleteTenantModalVisible(false)}
                title={t("DeleteTenant.title")}
                loading={okClicked || loading}
                drawbackPreventionMessage={
                    props.tenant.parentDrawbackPrevention ? (
                        <div className={classNames(form.warningBanner, form.fixedWidthModal)}>
                            <div className={classNames(form.formFields)}>
                                <Warning color={theme.secondaryWarningBackgroundColor} width={45} height={20} />
                                {t("DeleteTenant.drawbackPreventionWarningMessage")}
                            </div>
                        </div>
                    ) : undefined
                }
            />
            {deliveryCreationModalVisible && (
                <AddLicenseDeliveryView
                    tenantUuid={props.tenant.uuid}
                    tenantName={props.tenant.name}
                    onLicenseAdded={updateState}
                    onHideModal={updateStateWhenLicenseAdded}
                    tenantTier={props.tenant.tier}
                    tenantType={props.tenant.type}
                    licensingModel={props.tenant.licensingModel}
                    tenantRateUuid={props.tenant.rateUuid}
                    allowed={props.deliveryAllowed}
                    parentDrawbackPrevention={props.tenant.parentDrawbackPrevention}
                    contractType={props.tenant.contractType}
                />
            )}
            <Modal isOpen={subTenantModalVisible} hideModal={() => setSubTenantModalVisible(false)}>
                {okClicked ? (
                    <LoadingIndicator />
                ) : (
                    <>
                        {subTenantModalVisible ? (
                            <>
                                <div className={style.warningContainer}>
                                    <Warning color={theme.secondaryWarningBackgroundColor} />
                                </div>
                                <div className={style.subTenant}>
                                    <Heading tag={"h2"}>{t("DeleteTenant.subTenantMessage")}</Heading>
                                </div>
                                <div className={style.warningContainer}>{t("DeleteTenant.subTenantErrorMessage")}</div>
                                <div className={style.warningContainer}>
                                    <button
                                        className={buttons.primaryOkButton}
                                        onClick={() => setSubTenantModalVisible(false)}
                                        data-testid={testIds.common.dialog.closeButton}
                                    >
                                        {t("Common.ok")}
                                    </button>
                                </div>
                            </>
                        ) : (
                            ""
                        )}
                    </>
                )}
            </Modal>
            <Modal isOpen={retryModalVisible} hideModal={() => setRetryModalVisible(false)}>
                {okClicked ? (
                    <LoadingIndicator />
                ) : (
                    <>
                        {retryModalVisible ? (
                            <>
                                <div className={style.warningContainer}>
                                    <Warning color={theme.errorBackgroundColor} />
                                </div>
                                <div className={style.subTenant}>{t("DeleteTenant.deletionFailed")}</div>
                                <div className={style.warningContainer}>
                                    {t("DeleteTenant.deletionFailedMessage")}
                                    <span>
                                        <a href={mailToLink}>{t("DeleteTenant.supportLink")}</a>
                                    </span>
                                </div>
                                <div className={style.warningContainer}>
                                    {checkHierarchy ? (
                                        <button
                                            className={buttons.primaryOkButton}
                                            onClick={() => {
                                                setRetryModalVisible(false);
                                                setCheckHierarchy(false);
                                            }}
                                            data-testid={testIds.common.dialog.closeButton}
                                        >
                                            {t("Common.ok")}
                                        </button>
                                    ) : (
                                        <>
                                            <div className={buttons.buttonContainer}>
                                                <button
                                                    className={classNames(
                                                        buttons.primaryButtonWithoutIcon,
                                                        style.deleteButton
                                                    )}
                                                    data-testid={testIds.common.confirmationDialog.confirmButton}
                                                    onClick={handleTenantDelete}
                                                >
                                                    {t("Common.deleteAgain")}
                                                </button>
                                                <button
                                                    className={buttons.secondaryButtonWithoutIcon}
                                                    onClick={() => setRetryModalVisible(false)}
                                                    data-testid={testIds.common.dialog.closeButton}
                                                >
                                                    {t("Common.cancel")}
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </>
                        ) : (
                            ""
                        )}
                    </>
                )}
            </Modal>
            <Modal isOpen={loading} hideModal={() => setLoading(false)}>
                <LoadingIndicator />
            </Modal>
            <Modal
                isOpen={licenseNotAvailable}
                hideModal={hideErrorModal}
                modalTitle={t("AddCustomerView.subTenantCreationFailure")}
            >
                <>
                    <div className={style.errorMessageContainer}>
                        {t("AddCustomerView.licenseMissingError")}
                        <span>
                            <a href={mailToLink}>{t("AddCustomerView.supportText")}</a>
                        </span>
                    </div>
                    <div className={style.errorButtonContainer}>
                        <button
                            className={classNames(
                                buttons.primaryButton,
                                buttons.medium,
                                buttons.okButton,
                                buttons.buttonWithoutIcon
                            )}
                            onClick={hideErrorModal}
                            data-testid={testIds.common.dialog.closeButton}
                        >
                            {t("Common.ok")}
                        </button>
                    </div>
                </>
            </Modal>
            <MoveTenantDialog
                displayMoveTenantModal={moveTenantModalVisible}
                introductionMessage={
                    <div className={style.introductionMessage}>
                        {t("EditCustomerView.move.fromTenant")}
                        <b> &quot;{props.tenant.name}&quot; </b>
                        {t("EditCustomerView.move.toTenant")}
                    </div>
                }
                setDisplayMoveTenantModal={() => setMoveTenantModalVisible(false)}
                title={t("EditCustomerView.move.moveTenant")}
                loading={okClicked || loading}
                tenants={props.tenants}
                currentTenantId={props.tenant.uuid}
                customerName={props.tenant.name}
                currentTenantTier={props.tenant.tier}
                tenantUuid={props.tenant.uuid}
                onRefresh={props.onTenantEdit}
                currentTenantLicensingModel={props.tenant.licensingModel}
                currentTenantType={props.tenant.type}
            />
            {isMcConfigurationVisible && (
                <EditMcConfigurationLoader
                    hide={() => setIsMcConfigurationVisible(false)}
                    tenantUuid={props.tenant.uuid}
                    tenantRegion={props.tenant.region}
                />
            )}
        </>
    );
};
